import { useEffect, useState } from 'react'

// Baked into the bundle at build time (see vite.config.ts `define`).
declare const __BUILD_VERSION__: string

const POLL_INTERVAL_MS = 5 * 60 * 1000 // 5 minutes

/**
 * Detects when a newer version has been deployed while this tab has stayed open.
 * Compares the version baked into this bundle against `/version.json` (emitted at
 * build time, served no-cache), polling on an interval and whenever the tab
 * regains focus. Returns true once a newer build is live so the UI can offer a
 * reload. Fails silent in dev / offline (version.json may not exist).
 */
export function useVersionCheck(): boolean {
  const [updateAvailable, setUpdateAvailable] = useState(false)

  useEffect(() => {
    const current = typeof __BUILD_VERSION__ === 'string' ? __BUILD_VERSION__ : ''
    if (!current) return

    let cancelled = false

    const check = async () => {
      if (cancelled || updateAvailable) return
      try {
        const res = await fetch('/version.json', { cache: 'no-store' })
        if (!res.ok) return
        const data = await res.json()
        const latest = typeof data?.version === 'string' ? data.version : null
        if (!cancelled && latest && latest !== current) {
          setUpdateAvailable(true)
        }
      } catch {
        // offline / 404 in dev / transient — ignore and try again next tick
      }
    }

    const onVisible = () => {
      if (document.visibilityState === 'visible') check()
    }

    check()
    const interval = window.setInterval(check, POLL_INTERVAL_MS)
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', onVisible)

    return () => {
      cancelled = true
      window.clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', onVisible)
    }
    // updateAvailable intentionally excluded: once true we stop checking via the
    // guard above; re-running the effect would just re-add listeners.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return updateAvailable
}
